import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, CheckCircle, Clock, Phone, Navigation, X } from 'lucide-react';
import { dealersAPI } from '../services/api';
import { logger } from '../utils/logger';

const DealerSearchScreen = () => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  // Set when the search request fails, so it is not mistaken for "no results".
  const [searchFailed, setSearchFailed] = useState(false);

  // Auto-detect location on mount
  useEffect(() => {
    requestLocation();
  }, []);

  const requestLocation = () => {
    if (!('geolocation' in navigator)) return;
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      () => {
        setLocationLoading(false);
      },
      { timeout: 10000 }
    );
  };

  const handleSearch = useCallback(async (searchQuery, coords) => {
    setLoading(true);
    setHasSearched(true);
    setSearchFailed(false);
    try {
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (coords) {
        params.lat = coords.lat;
        params.lng = coords.lng;
      }
      const response = await dealersAPI.search(params);
      setDealers(response.data);
    } catch (error) {
      logger.error('Dealer search error', { message: error?.message });
      setDealers([]);
      setSearchFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search when location is obtained (initial load)
  useEffect(() => {
    if (userLocation) {
      handleSearch(query, userLocation);
    }
  }, [userLocation]);

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch(query, userLocation);
  };

  const clearSearch = () => {
    setQuery('');
    handleSearch('', userLocation);
  };

  // Format address
  const formatAddress = (address) => {
    if (!address) return 'Address not available';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city) parts.push(address.city);
    if (address.state && address.zip) {
      parts.push(`${address.state} ${address.zip}`);
    } else if (address.state) {
      parts.push(address.state);
    }
    return parts.join(', ') || 'Address not available';
  };

  // Check if dealer is open now
  const isOpenNow = (hours) => {
    if (!hours || hours.length === 0) return null;
    const now = new Date();
    const dayOfWeek = now.getDay();
    const currentTime = now.toTimeString().slice(0, 5);
    const todayHours = hours.find(h => h.day === dayOfWeek);
    if (!todayHours || !todayHours.isOpen) return false;
    return currentTime >= todayHours.open && currentTime <= todayHours.close;
  };

  const handleViewDealer = (dealerId) => {
    navigate(`/vehicles?dealerId=${dealerId}`);
  };

  return (
    <div className="min-h-screen bg-app-bg-light dark:bg-app-bg">
      {/* Header */}
      <div className="bg-gradient-to-br from-app-accent/10 via-blue-50 to-app-accent/5 dark:from-app-accent/10 dark:via-app-bg dark:to-app-accent/5 py-10 lg:py-14">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl lg:text-4xl font-bold text-app-text-light dark:text-app-text mb-2 text-center">
            Find a Dealership
          </h1>
          <p className="text-lg text-app-text-secondary-light dark:text-app-text-secondary text-center mb-8">
            Search by dealership name, city, or state to find verified dealers near you
          </p>

          {/* Search Form */}
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-app-text-secondary-light dark:text-app-text-secondary" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, city, or state..."
                className="w-full pl-12 pr-10 py-4 rounded-xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text border border-app-border-light dark:border-app-border focus:outline-none focus:ring-2 focus:ring-app-accent placeholder:text-app-text-secondary-light dark:placeholder:text-app-text-secondary text-lg"
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-app-bg-tertiary-light dark:hover:bg-app-bg-tertiary rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-app-text-secondary-light dark:text-app-text-secondary" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="px-6 py-4 bg-app-accent hover:bg-app-accent-hover text-white rounded-xl font-semibold transition-all duration-200 hover:shadow-lg flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </form>

          {/* Location status */}
          <div className="flex items-center justify-center gap-2 mt-4 text-sm">
            {locationLoading ? (
              <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                Detecting your location...
              </span>
            ) : userLocation ? (
              <span className="flex items-center gap-1 text-app-success">
                <Navigation className="w-4 h-4" />
                Sorting by distance from your location
              </span>
            ) : (
              <button
                onClick={requestLocation}
                className="flex items-center gap-1 text-app-accent hover:text-app-accent-hover transition-colors"
              >
                <MapPin className="w-4 h-4" />
                Enable location for nearest results
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-app-accent border-t-transparent"></div>
            <p className="mt-4 text-app-text-secondary-light dark:text-app-text-secondary">Searching dealerships...</p>
          </div>
        ) : dealers.length > 0 ? (
          <>
            <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-6">
              {dealers.length} dealership{dealers.length !== 1 ? 's' : ''} found
              {query && <> matching "<span className="font-medium">{query}</span>"</>}
            </p>
            <div className="space-y-4">
              {dealers.map((dealer) => {
                const openStatus = isOpenNow(dealer.hours);
                return (
                  <div
                    key={dealer._id}
                    className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-6 hover:border-app-accent-light dark:hover:border-app-accent transition-all duration-200 hover:shadow-lg"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      {/* Dealer info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-app-text-light dark:text-app-text">
                            {dealer.name}
                          </h3>
                          {dealer.verified && (
                            <CheckCircle className="w-5 h-5 text-app-success" title="Verified Dealer" />
                          )}
                        </div>

                        {/* Address */}
                        <div className="flex items-start gap-1.5 text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{formatAddress(dealer.address)}</span>
                        </div>

                        {/* Phone */}
                        {dealer.phone && (
                          <div className="flex items-center gap-1.5 text-sm text-app-text-secondary-light dark:text-app-text-secondary mb-2">
                            <Phone className="w-4 h-4" />
                            <a
                              href={`tel:${dealer.phone}`}
                              className="hover:text-app-accent transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {dealer.phone}
                            </a>
                          </div>
                        )}

                        {/* Open status & distance */}
                        <div className="flex items-center gap-4 mt-3">
                          {openStatus !== null && (
                            <div className="flex items-center gap-1">
                              <Clock className={`w-4 h-4 ${openStatus ? 'text-app-success' : 'text-app-error'}`} />
                              <span className={`text-sm font-medium ${openStatus ? 'text-app-success' : 'text-app-error'}`}>
                                {openStatus ? 'Open Now' : 'Closed'}
                              </span>
                            </div>
                          )}
                          {dealer.distanceMiles != null && (
                            <span className="text-sm font-medium text-app-accent flex items-center gap-1">
                              <Navigation className="w-3.5 h-3.5" />
                              {dealer.distanceMiles} mi away
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side: rating + CTA */}
                      <div className="flex sm:flex-col items-center sm:items-end gap-3">
                        {dealer.rating && dealer.rating.count > 0 && (
                          <div className="flex items-center gap-1 bg-app-warning/10 px-3 py-1.5 rounded-lg">
                            <Star className="w-4 h-4 fill-app-warning text-app-warning" />
                            <span className="text-sm font-semibold text-app-text-light dark:text-app-text">
                              {dealer.rating.average.toFixed(1)}
                            </span>
                            <span className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">
                              ({dealer.rating.count})
                            </span>
                          </div>
                        )}
                        <button
                          onClick={() => handleViewDealer(dealer._id)}
                          className="px-5 py-2.5 bg-app-accent hover:bg-app-accent-hover text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg text-sm whitespace-nowrap"
                        >
                          View Vehicles
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : hasSearched ? (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-app-text-secondary-light dark:text-app-text-secondary mx-auto mb-4 opacity-40" />
            <h3 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-2">
              {searchFailed ? "We couldn't load dealerships" : 'No dealerships found'}
            </h3>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary mb-6">
              {searchFailed
                ? 'Something went wrong on our side. Please try again in a moment.'
                : query
                ? `We couldn't find any verified dealerships matching "${query}". Try a different search term.`
                : 'Try searching by city, state, or dealership name.'}
            </p>
            {query && (
              <button
                onClick={clearSearch}
                className="px-6 py-3 bg-app-accent hover:bg-app-accent-hover text-white rounded-xl font-medium transition-all"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-app-text-secondary-light dark:text-app-text-secondary mx-auto mb-4 opacity-40" />
            <h3 className="text-xl font-semibold text-app-text-light dark:text-app-text mb-2">
              Search for dealerships
            </h3>
            <p className="text-app-text-secondary-light dark:text-app-text-secondary">
              Enter a dealership name, city, or state to get started.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerSearchScreen;
